import JSZip from 'jszip';
import ToastifyNotification from 'utils/toast/ToastifyNotification';

const acceptedImgsFormat = ['image/png', 'image/jpeg'];
const acceptedZipFormat = [
  'application/zip',
  'application/x-zip-compressed',
  'multipart/x-zip'
];

const acceptedDocsFormat = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];

export const testFilesFormat = async (files, newFiles, variant = 'normal') => {
  const all = [...files, ...newFiles];
  const validFiles = [];
  const zipCount = all.filter(
    f =>
      acceptedZipFormat.includes(f.type) ||
      f.name.toLowerCase().endsWith('.zip')
  ).length;

  const hasZip = all.some(file => acceptedZipFormat.includes(file.type));
  const hasDocs = all.some(file => acceptedDocsFormat.includes(file.type));
  const newHasZip = newFiles.some(file =>
    acceptedZipFormat.includes(file.type)
  );
  const newHasImages = newFiles.some(file =>
    acceptedDocsFormat.includes(file.type)
  );

  if (hasZip && hasDocs) {
    ToastifyNotification({
      message:
        'You cannot upload both ZIP and individual docs. Please choose one type.',
      errorMessage: true
    });

    if (newHasZip) {
      return files;
    }

    if (newHasImages) {
      return files;
    }
  }

  for (const file of newFiles) {
    const isZip =
      acceptedZipFormat.includes(file.type) ||
      file.name.toLowerCase().endsWith('.zip');

    if (isZip) {
      if (zipCount > 1) {
        ToastifyNotification({
          message: 'You can upload only one ZIP archive',
          errorMessage: true
        });

        return all.slice(0, 1);
      }

      try {
        const zip = await JSZip.loadAsync(file);
        const entries = Object.keys(zip.files);

        const hasFolder = entries.some(entry => zip.files[entry].dir);
        const nonDocsFiles = entries.filter(
          entry =>
            !zip.files[entry].dir &&
            !entry.toLowerCase().match(/\.(pdf|docx|xlsx)$/)
        );

        if (hasFolder) {
          ToastifyNotification({
            message: 'ZIP must not contain folders',
            errorMessage: true
          });
          return validFiles;
        }

        if (nonDocsFiles.length > 0) {
          ToastifyNotification({
            message: 'ZIP must contain only .pdf, .docx, .xlsx formats',
            errorMessage: true
          });
          return validFiles;
        }

        validFiles.push(file);
      } catch (error) {
        console.error('Error while reading ZIP archive', error);
        ToastifyNotification({
          message: 'Error while reading ZIP archive',
          errorMessage: true
        });
      }
    } else if (acceptedDocsFormat.includes(file.type)) {
      validFiles.push(file);
    } else {
      ToastifyNotification({
        message: 'You can drop only .pdf, .docx, .xlsx or .zip formats',
        errorMessage: true
      });
      return validFiles;
    }
  }

  const combined = [...files, ...validFiles];
  const length = variant === 'normal' ? 9 : 3;
  if (combined.length > length) {
    ToastifyNotification({
      message: `You can drop only ${length} documents at a time`,
      errorMessage: true
    });
    return combined.slice(0, length);
  }

  return combined;
};

export const testImagesFormat = async (images, newImages) => {
  const all = [...images, ...newImages];
  const zipCount = all.filter(
    f =>
      acceptedZipFormat.includes(f.type) ||
      f.name.toLowerCase().endsWith('.zip')
  ).length;

  const hasZip = all.some(file => acceptedZipFormat.includes(file.type));
  const hasImages = all.some(file => acceptedImgsFormat.includes(file.type));

  const newHasZip = newImages.some(file =>
    acceptedZipFormat.includes(file.type)
  );
  const newHasImages = newImages.some(file =>
    acceptedImgsFormat.includes(file.type)
  );

  if (hasZip && hasImages) {
    ToastifyNotification({
      message:
        'You cannot upload both ZIP and individual images. Please choose one type.',
      errorMessage: true
    });

    if (newHasZip) {
      return images;
    }

    if (newHasImages) {
      return images;
    }
  }

  const validFiles = [];

  for (const file of newImages) {
    const isZip =
      acceptedZipFormat.includes(file.type) ||
      file.name.toLowerCase().endsWith('.zip');

    if (isZip) {
      if (zipCount > 1) {
        ToastifyNotification({
          message: 'You can upload only one ZIP archive',
          errorMessage: true
        });

        return all.slice(0, 1);
      }

      try {
        const zip = await JSZip.loadAsync(file);
        const entries = Object.keys(zip.files);

        const hasFolder = entries.some(entry => zip.files[entry].dir);
        const nonImageFiles = entries.filter(
          entry =>
            !zip.files[entry].dir &&
            !entry.toLowerCase().match(/\.(png|jpg|jpeg)$/)
        );

        if (hasFolder) {
          ToastifyNotification({
            message: 'ZIP must not contain folders',
            errorMessage: true
          });
          return validFiles;
        }

        if (nonImageFiles.length > 0) {
          ToastifyNotification({
            message: 'ZIP must contain only .png or .jgp formats',
            errorMessage: true
          });
          return validFiles;
        }

        validFiles.push(file);
      } catch (error) {
        console.error('Error while reading ZIP archive', error);
        ToastifyNotification({
          message: 'Error while reading ZIP archive',
          errorMessage: true
        });
      }
    } else if (acceptedImgsFormat.includes(file.type)) {
      validFiles.push(file);
    } else {
      ToastifyNotification({
        message: 'You can drop only .png, .jpg or .zip formats',
        errorMessage: true
      });
      return validFiles;
    }
  }

  const combined = [...images, ...validFiles];

  if (combined.length > 9) {
    ToastifyNotification({
      message: 'You can drop only 9 images at a time',
      errorMessage: true
    });
    return combined.slice(0, 9);
  }

  return combined;
};

export const testFloorPlanFormat = (file, newFiles) => {
  const validFiles = [];

  for (const file of newFiles) {
    if (acceptedDocsFormat[0].includes(file.type)) {
      validFiles.push(file);
    } else {
      ToastifyNotification({
        message: 'You can drop only .pdf formats',
        errorMessage: true
      });
      return validFiles;
    }
  }

  const combined = [...file, ...validFiles];

  if (combined.length > 1) {
    ToastifyNotification({
      message: 'You can drop only 1 file at a time',
      errorMessage: true
    });
    return combined.slice(0, 1);
  }

  return combined;
};

export const testShopFitFormat = async (files, newFiles) => {
  const all = [...files, ...newFiles];
  const validFiles = [];
  const zipCount = all.filter(
    f =>
      acceptedZipFormat.includes(f.type) ||
      f.name.toLowerCase().endsWith('.zip')
  ).length;

  const hasZip = all.some(file => acceptedZipFormat.includes(file.type));
  const hasDocs = all.some(file => acceptedDocsFormat.includes(file.type));
  const newHasZip = newFiles.some(file =>
    acceptedZipFormat.includes(file.type)
  );
  const newHasImages = newFiles.some(file =>
    acceptedDocsFormat.includes(file.type)
  );

  if (hasZip && hasDocs) {
    ToastifyNotification({
      message:
        'You cannot upload both ZIP and individual docs. Please choose one type.',
      errorMessage: true
    });

    if (newHasZip) {
      return files;
    }

    if (newHasImages) {
      return files;
    }
  }

  for (const file of newFiles) {
    const isZip =
      acceptedZipFormat.includes(file.type) ||
      file.name.toLowerCase().endsWith('.zip');

    if (isZip) {
      if (zipCount > 1) {
        ToastifyNotification({
          message: 'You can upload only one ZIP archive',
          errorMessage: true
        });

        return all.slice(0, 1);
      }

      try {
        const zip = await JSZip.loadAsync(file);
        const entries = Object.keys(zip.files);

        const hasDocumentsFolder = entries.some(
          entry => entry.toLowerCase() === 'documents/'
        );
        if (!hasDocumentsFolder) {
          ToastifyNotification({
            message: 'ZIP archive must contain root "documents" folder',
            errorMessage: true
          });
          return validFiles;
        }

        const allowedFileRegex = /\.(pdf|docx|xlsx)$/;
        const validFilesInsideDocuments = entries.filter(entry => {
          const file = zip.files[entry];
          const isFile = !file.dir;
          const isInDocuments = entry.toLowerCase().startsWith('documents/');
          const isAllowed = allowedFileRegex.test(entry.toLowerCase());

          return isFile && isInDocuments && isAllowed;
        });

        if (validFilesInsideDocuments.length === 0) {
          ToastifyNotification({
            message:
              'The "documents" folder must contain at least one file with extension .pdf, .docx or .xlsx',
            errorMessage: true
          });
          return validFiles;
        }

        const invalidFiles = entries.filter(entry => {
          const file = zip.files[entry];
          const isFile = !file.dir;
          const isInDocuments = entry.toLowerCase().startsWith('documents/');
          const isAllowed = allowedFileRegex.test(entry.toLowerCase());

          return isFile && isInDocuments && !isAllowed;
        });

        if (invalidFiles.length > 0) {
          ToastifyNotification({
            message:
              'Only .pdf, .docx and .xlsx files are allowed inside "documents"',
            errorMessage: true
          });
          return validFiles;
        }

        validFiles.push(file);
      } catch (error) {
        console.error('Error while reading ZIP archive', error);
        ToastifyNotification({
          message: 'Error while reading ZIP archive',
          errorMessage: true
        });
      }
    } else if (acceptedDocsFormat.includes(file.type)) {
      validFiles.push(file);
    } else {
      ToastifyNotification({
        message: 'You can drop only .pdf, .docx, .xlsx or .zip formats',
        errorMessage: true
      });
      return validFiles;
    }
  }

  const combined = [...files, ...validFiles];

  if (combined.length > 3) {
    ToastifyNotification({
      message: 'You can drop only 3 documents at a time',
      errorMessage: true
    });
    return combined.slice(0, 3);
  }

  return combined;
};

export const testNewsFormat = async (images, newImages) => {
  const all = [...images, ...newImages];
  const validFiles = [];

  for (const file of newImages) {
    if (acceptedImgsFormat.includes(file.type)) {
      validFiles.push(file);
    } else {
      ToastifyNotification({
        message: 'You can drop only .png, or .jpg formats',
        errorMessage: true
      });
      return images;
    }
  }

  const combined = [...images, ...validFiles];

  if (combined.length > 1) {
    ToastifyNotification({
      message: 'You can drop only 1 images at a time',
      errorMessage: true
    });
    return combined.slice(0, 1);
  }

  return combined;
};
